import { useEffect, useState } from 'react';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import MarketSelect from '@/components/base/MarketSelect';
import { roleTone } from '@/utils/staff';
import type { RoleRecord } from '@/types/portal';

interface InviteUserModalProps {
  open: boolean;
  roles: RoleRecord[];
  onClose: () => void;
  onInvite: (name: string, email: string, roleId: string, markets: string[]) => void;
  /** The new user's temporary password; production sends no invite email. */
  temporaryPassword?: string;
  /** Last save error, shown above the form. */
  error?: string;
  done: boolean;
  invitedName: string;
  invitedEmail: string;
  /** Shown after Resend: the API replaced the user's temporary password. */
  resent?: boolean;
}

export default function InviteUserModal({
  open,
  roles,
  onClose,
  onInvite,
  done,
  invitedName,
  invitedEmail,
  temporaryPassword = '',
  error = '',
  resent = false,
}: InviteUserModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState((roles.find((role) => role.id !== 'admin') ?? roles[0])?.id ?? '');
  const [markets, setMarkets] = useState<string[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setRoleId((roles.find((role) => role.id !== 'admin') ?? roles[0])?.id ?? '');
      setMarkets([]);
      setNote('');
    }
  }, [open, roles]);

  const selectedRole = roles.find((role) => role.id === roleId);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const valid = name.trim().length > 0 && emailValid && markets.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={done ? 'ri-checkbox-circle-line' : 'ri-user-add-line'}
      title={done ? (resent ? 'New temporary password' : temporaryPassword ? 'User added' : 'Invitation sent') : 'Invite a user'}
      subtitle={done ? invitedEmail : 'Give a teammate or client access to this portal'}
      widthClassName="max-w-lg"
      footer={
        done ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              icon="ri-send-plane-line"
              disabled={!valid}
              onClick={() => onInvite(name.trim(), email.trim(), roleId, markets)}
            >
              Send invite
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="flex flex-col items-center py-2 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-700">
            <i className="ri-mail-send-line" />
          </span>
          {temporaryPassword ? (
            <>
              {resent ? (
                <p className="mt-3 text-sm text-foreground-700">
                  <span className="font-medium text-foreground-950">{invitedName}</span>&apos;s earlier temporary
                  password no longer works. Send them this one; they must set a new password at first sign-in.
                </p>
              ) : (
                <p className="mt-3 text-sm text-foreground-700">
                  <span className="font-medium text-foreground-950">{invitedName}</span> can sign in now with
                  this temporary password. They&apos;ll appear as <span className="font-medium">Invited</span>{' '}
                  until their first sign-in, when they must set a new one.
                </p>
              )}
              <div className="mt-3 w-full rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3">
                <p className="text-[11px] font-label font-semibold uppercase tracking-wider text-secondary-900">
                  Temporary password
                </p>
                <p className="mt-1 select-all font-label text-lg font-semibold tracking-wide text-foreground-950">
                  {temporaryPassword}
                </p>
                <p className="mt-1 text-[11px] text-secondary-900">
                  Share it securely: invite emails aren&apos;t sent yet, and it won&apos;t be shown again.
                </p>
              </div>
            </>
          ) : (
            <p className="mt-3 text-sm text-foreground-700">
              We sent an invitation to <span className="font-medium text-foreground-950">{invitedName}</span>.
              They&apos;ll appear as <span className="font-medium">Invited</span> until they accept.
            </p>
          )}
          <div className="mt-4 w-full rounded-md border border-background-200 bg-background-100 px-4 py-3 text-left">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground-500">Role</span>
              <Badge tone={selectedRole ? roleTone(selectedRole.id) : 'neutral'}>
                {selectedRole?.name ?? '—'}
              </Badge>
            </div>
            <div className="mt-2 flex items-start justify-between gap-3 text-sm">
              <span className="text-foreground-500">Markets</span>
              <span className="text-right text-foreground-800">{markets.join(', ')}</span>
            </div>
            <p className="mt-2 text-[11px] text-foreground-500">
              {temporaryPassword
                ? 'Password change required at first sign-in'
                : 'Invite expires in 7 days · sent by Peter Kironji'}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {error ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800"
            >
              <i className="ri-error-warning-line mt-0.5" />
              <span>{error}</span>
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="invite-name"
                className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
              >
                Full name
              </label>
              <input
                id="invite-name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Jane Doe"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="invite-email"
                className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
              >
                Work email
              </label>
              <input
                id="invite-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@kilicreatives.co.ke"
                className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="invite-role"
              className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
            >
              Role
            </label>
            <div className="relative">
              <select
                id="invite-role"
                value={roleId}
                onChange={(event) => setRoleId(event.target.value)}
                className="h-10 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm font-label text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
            </div>
            {selectedRole?.id === 'admin' ? (
              <p className="mt-1.5 text-[11px] text-primary-700">
                Admins have every permission, including sending payments and managing users.
              </p>
            ) : selectedRole?.description ? (
              <p className="mt-1.5 text-[11px] text-foreground-500">
                {selectedRole.description}
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-label font-medium text-foreground-700">
              Market access
            </p>
            <MarketSelect
              value={markets}
              onChange={setMarkets}
              placeholder="Search African countries…"
            />
            {markets.length === 0 ? (
              <p className="mt-1.5 text-[11px] text-foreground-400">
                Pick at least one market this user can operate in.
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="invite-note"
              className="mb-1.5 block text-xs font-label font-medium text-foreground-700"
            >
              Note <span className="text-foreground-400">(optional)</span>
            </label>
            <textarea
              id="invite-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              maxLength={200}
              placeholder="Shown in the invitation email"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}